import React, { useContext, useRef, useMemo } from 'react';
import { useAsync } from 'react-commons';
import { Column, TableInstance } from 'react-table';
import { Button } from 'react-bootstrap';
import { Link, RouteComponentProps } from 'react-router-dom';
import ConfigContext from '../../../contexts/ConfigContext';
import ErrorBlock from '../../common/ErrorBlock';
import Spinner from '../../common/Spinner';
import { formatAmDisplayString, formatDate, formatExportText } from '../../../utils/displayUtils';
import DataTable from '../../common/datatables/DataTable';
import { getProjectErrors, deleteProjectError } from '../../../api/projectErrorApi';
import { ProjectErrorType } from '../../../api/projectErrorApiTypes';
import DeleteConfirmationModal from '../../common/modal/DeleteConfirmationModal';
import SyncProjectModal from '../../common/modal/SyncProjectModal';
import { DATE_TIME_FORMAT, DEFAULT_ADMIN_INITIAL_STATE } from '../../../utils/Constants';
import { generateCSV } from '../../common/listUtils';
import useStateWithSessionStorage from '../../../hooks/useStateWithSessionStorage';

const ProjectErrorHandling = (routerProps: RouteComponentProps) => {
    const config = useContext(ConfigContext);

    const [initialState, setInitialState] = useStateWithSessionStorage(
        'projectErrorsInitialState',
        JSON.stringify(DEFAULT_ADMIN_INITIAL_STATE),
    );

    const {
        value: projectErrors,
        error,
        loading,
        refresh,
    } = useAsync(() => getProjectErrors(config), [] as Array<ProjectErrorType>);

    const tableInstanceRef = useRef<TableInstance<ProjectErrorType> | null>(null);

    if (error) {
        return typeof error === 'object' ? (
            <ErrorBlock errorMessage="Network Error!" />
        ) : (
            <ErrorBlock errorMessage={error} />
        );
    }

    const projectErrorOptions = (projectError: ProjectErrorType) => (
        <div className="btn-group options-icon">
            <button
                type="button"
                className="btn btn-link p-0"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
            >
                <span className="fas fa-ellipsis-h text-muted" />
            </button>

            <ul className="dropdown-menu dropdown-menu-right">
                <li>
                    <button
                        type="button"
                        className="dropdown-menu-modal-toggle"
                        onClick={() => {
                            routerProps.history.push({
                                pathname: `/details/${projectError.amId}`,
                                state: {
                                    returnPageName: 'Project Errors',
                                    returnPathName: '/admin/projectErrorHandling',
                                },
                            });
                        }}
                    >
                        <i className="far fa-info-circle button-icon" /> AM Details
                    </button>
                </li>
                <li role="separator" className="dropdown-divider" />
                <li>
                    <SyncProjectModal
                        amId={projectError.amId}
                        toggleButton={
                            <Button
                                variant="button"
                                className="dropdown-menu-modal-toggle dropdown-item"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                Sync Project
                            </Button>
                        }
                        onSubmit={refresh}
                    />
                </li>
                <li>
                    <DeleteConfirmationModal
                        title="Delete Project Error"
                        onConfirm={async () => {
                            await deleteProjectError(config, projectError.id);
                            refresh();
                        }}
                        body={
                            <>
                                <p>
                                    Please confirm that you would like to delete this project error.
                                </p>
                                <p>
                                    Deleting this error will not prevent it from reoccuring if the
                                    original issue has not been corrected.
                                </p>
                            </>
                        }
                        toggleButton={
                            <Button
                                variant="button"
                                className="dropdown-menu-modal-toggle dropdown-item"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                <span className="text-danger">Delete Project Error</span>
                            </Button>
                        }
                        confirmButtonText="Confirm Delete"
                    />
                </li>
            </ul>
        </div>
    );

    const columns = [
        {
            Header: 'AM#',
            id: 'amNumber',
            width: 100,
            Cell: (cellProps: {
                row: { original: { amId: number; amNumber: number; supNumber: number } };
            }) =>
                useMemo(
                    () => (
                        <Link
                            to={{
                                pathname: `/details/${cellProps.row.original.amId}`,
                                state: {
                                    returnPageName: 'Project Errors',
                                    returnPathName: '/admin/projectErrorHandling',
                                },
                            }}
                        >
                            {formatAmDisplayString(
                                cellProps.row.original.amNumber,
                                cellProps.row.original.supNumber,
                            )}
                        </Link>
                    ),
                    [],
                ),
        },
        {
            Header: 'Error Message',
            accessor: 'errorMessage',
            width: null,
        },
        {
            Header: 'Status',
            accessor: 'amStatus',
            width: 200,
        },
        {
            Header: 'Error Date',
            accessor: 'errorDate',
            width: 300,
            Cell: (cellProps: { value: string }) =>
                useMemo(() => <span>{formatDate(cellProps.value, DATE_TIME_FORMAT)}</span>, []),
        },
        {
            id: 'options',
            Header: '',
            className: 'table-cell-pointer overflow-visible',
            Cell: (cellProps: { row: { original: ProjectErrorType } }) =>
                projectErrorOptions(cellProps.row.original),
            disableSortBy: true,
            width: 55,
        },
    ];

    return (
        <div className="container-fluid mt-3">
            <div className="row row-page-header">
                <div className="col-12 d-flex align-items-center mb-2">
                    <h3 className="mr-auto text-weight-normal mb-0">Project Error</h3>
                    <div className="pull-right">
                        <Button
                            variant="outline-primary"
                            disabled={loading || projectErrors.length <= 0}
                            onClick={() => {
                                if (tableInstanceRef.current) {
                                    const list = tableInstanceRef.current.rows
                                        .map((row) => row.original)
                                        .map((projectError) => ({
                                            'Action Memo #': projectError.amNumber,
                                            'Supplement #': projectError.supNumber,
                                            'Error Message': formatExportText(
                                                projectError.errorMessage,
                                            ),
                                            Status: projectError.amStatus,
                                            'Error Date': projectError.errorDate
                                                ? formatDate(
                                                      projectError.errorDate,
                                                      'YYYY-MM-DD HH:mm:ss',
                                                  )
                                                : '',
                                        }));
                                    generateCSV(list, 'Project Errors.csv');
                                }
                            }}
                        >
                            <i className="fas fa-file-export" /> Export CSV
                        </Button>
                    </div>
                </div>
            </div>

            <div className="card border-top">
                <div className="card-body">
                    {!loading ? (
                        <DataTable<ProjectErrorType>
                            columns={columns as Column<ProjectErrorType>[]}
                            data={projectErrors}
                            initialState={JSON.parse(initialState)}
                            setInitialState={setInitialState}
                            noGlobalFilter
                            getInstanceCallback={(tableInstance) => {
                                if (tableInstanceRef) {
                                    tableInstanceRef.current = tableInstance;
                                }
                            }}
                        />
                    ) : (
                        <Spinner />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProjectErrorHandling;
